import { useState, useEffect } from "react";
import { Backdrop, CircularProgress } from "@mui/material";
import ToastMessage from "../../utils/mui/righttoptoast";
import SearchIcon from '@mui/icons-material/Search';
import api from "../../api/api";
import { debounce } from 'lodash';
import dayjs from "dayjs";
import { getPracticeId } from "../../config/practice";

const ManualEntry = ({ closeManualEntryModal, fetchtimesheetdata, start_date, empid }) => {
  const [searchTerm, setSearchTerm] = useState(empid || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allUsers, setAllUsers] = useState([]); // To hold all users
  const [filteredUsers, setFilteredUsers] = useState([]); // To hold filtered users based on search
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [clockInDate, setClockInDate] = useState(start_date); // Set default date here
  const [clockInTime, setClockInTime] = useState('');
  const [clockOutTime, setClockOutTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');
  const practiseid = getPracticeId();
  
  // Fetch the list of users when the component mounts
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/v1/${practiseid}/users`); 
        if (response.data && Array.isArray(response.data)) {
          const filteredData = response.data.filter(item => item?.config?.enrolled_id);
          setAllUsers(filteredData); 
          setFilteredUsers(filteredData); 
        } else {
          setAllUsers([]); 
          setFilteredUsers([]); 
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        setAllUsers([]); 
        setFilteredUsers([]); 
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [practiseid]); // Ensure practiseid is included as a dependency if it might change

useEffect(() => {
  if (allUsers.length > 0 && empid) {
    const employee = allUsers.find((user) => user.id === empid);
    if (employee) {
      setSelectedEmployee(employee);
      setSearchTerm(`${employee.firstname} ${employee.lastname}`);
      setShowSuggestions(false);
      setFilteredUsers([]);
    }
  }
}, [allUsers, empid]);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchTerm(query);
    filterSuggestions(query);
    if (!query) {
      setShowSuggestions(false);
      setFilteredUsers(allUsers); // Show all users if search is cleared
    } else {
      setShowSuggestions(true);
    }
  };

  const filterSuggestions = debounce((query) => {
    if (!query) {
      setFilteredUsers(allUsers); // If no search term, show all users
      return;
    }
    const filtered = allUsers.filter((item) =>
      `${item.firstname} ${item.lastname}`.toLowerCase().includes(query)
    );
    setFilteredUsers(filtered);
  }, 500);

  const handleSuggestionClick = (employee) => {
    setSelectedEmployee(employee);
    setSearchTerm(`${employee.firstname} ${employee.lastname}`);
    setShowSuggestions(false);
    setFilteredUsers([]); // Clear suggestions after selecting an employee
  };

  const handleClockInDateChange = (e) => setClockInDate(e.target.value);
  const handleClockInChange = (e) => setClockInTime(e.target.value);
  const handleClockOutChange = (e) => setClockOutTime(e.target.value);

  const convertToFullTimestamp = (date, time) => {
    // Combine the selected date and time without converting to UTC
    return dayjs(`${date}T${time}:00`).format(); // Just format as a valid ISO string in local time
  };

  const handleSubmit = async () => {
    if (!selectedEmployee) {
      setToastMessage('Please select an employee');
      setToastSeverity('error');
      setOpenToast(true);
      return;
    }

    if (!clockInDate || !clockInTime || !clockOutTime) {
      setToastMessage('Please select both clock-in and clock-out times');
      setToastSeverity('error');
      setOpenToast(true);
      return;
    }

    const clockInFullTimestamp = convertToFullTimestamp(clockInDate, clockInTime);
    const clockOutFullTimestamp = convertToFullTimestamp(clockInDate, clockOutTime);

    setLoading(true);
    try {
      const response = await api.post(`/v1/${practiseid}/timesheet/manual-entry`, {
        emp_id: selectedEmployee.id,
        emp_name: `${selectedEmployee.firstname} ${selectedEmployee.lastname}`,
        emp_role: '',      
        employee_type: selectedEmployee.employeeType || 'Hourly',
        work_date: clockInDate,
        clock_in_time: clockInFullTimestamp,
        clock_out_time: clockOutFullTimestamp,
        break_minutes: 0,
        notes: '',
        clockin_pic: '',
        clockout_pic: '',
        hire_date: '',
        status: 'A'
      });
      if (response.status === 200) {
        setToastMessage('Clock-in/Clock-out times recorded successfully!');
        setToastSeverity('success');
        setOpenToast(true);
        setClockInTime('');
        setClockOutTime('');
        setSelectedEmployee(null);
        fetchtimesheetdata();
        closeManualEntryModal();
      } else {
        setToastMessage('Failed to record clock-in/out times');
        setToastSeverity('error');
        setOpenToast(true);
      }
    } catch (error) {
      // 403 / 409 from the attendance rules (privilege, automatic clock-out on that day, own hours): show why
      setToastMessage(error?.response?.data?.message || 'Something went wrong');
      setToastSeverity('error');
      setOpenToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleToastClose = () => setOpenToast(false);

  return (
    <div className="flex flex-col p-4">
      {/* Search Bar */}
      <div className="relative flex items-center bg-gray-100 border border-gray-300 rounded py-1 px-2 mb-4">
        <SearchIcon className="text-neutral-500" />
        <input
          type="text"
          placeholder="Enter Employee Name or ID"
          className="w-full text-sm bg-transparent border-none outline-none pl-2 text-neutral-700 text-start"
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>

      {/* Suggestions */}
      {showSuggestions && (
        <div className="absolute z-10 w-full bg-white border border-neutral-300 rounded mt-1 max-h-60 overflow-y-auto mt-5">
          {filteredUsers.length > 0 ? (
            filteredUsers.map((item) => (
              <div
                key={item.id}
                className="p-2 text-start cursor-pointer font-semibold text-[#989898] text-sm transition-colors duration-200 hover:bg-neutral-100 hover:text-blue-600"
                onClick={() => handleSuggestionClick(item)}
              >
                {item.firstname} {item.lastname}
              </div>
            ))
          ) : (
            searchTerm && (
              <div className="p-2 text-start text-sm text-gray-500">No data available</div>
            )
          )}
        </div>
      )}

      {/* Employee Info */}
      {selectedEmployee && (
        <div className="my-4 text-start">
          <label className="font-semibold text-start">Selected Employee : </label>
          {selectedEmployee.firstname} {selectedEmployee.lastname}
        </div>
      )}

      {/* Clock-in Date Picker */}
      <div className="my-4 text-start">
        <label className="block text-sm font-medium text-[#232323] mb-2">Clock-in Date</label>
        <input
          type="date"
          value={clockInDate}
          onChange={handleClockInDateChange}
          className="w-full text-start px-4 py-2 rounded-[10px] border-[1px] border-[#DFEAF2] bg-white shadow-[0px_2px_4px_0px_rgba(66,80,102,0.10)]"
        />
      </div>

      {/* Clock-in/Clock-out Times */}
      <div className="my-4 text-start">
        <label className="block text-sm font-medium text-[#232323] mb-2">Clock-in Time</label>
        <input
          type="time"
          value={clockInTime}
          onChange={handleClockInChange}
          className="w-full px-4 py-2 rounded-[10px] border-[1px] border-[#DFEAF2] bg-white shadow-[0px_2px_4px_0px_rgba(66,80,102,0.10)]"
        />
      </div>

      <div className="my-4 text-start">
        <label className="block text-sm font-medium text-[#232323] mb-2">Clock-out Time</label>
        <input
          type="time"
          value={clockOutTime}
          onChange={handleClockOutChange}
          className="w-full px-4 py-2 rounded-[10px] border-[1px] border-[#DFEAF2] bg-white shadow-[0px_2px_4px_0px_rgba(66,80,102,0.10)]"
        />
      </div>

      {/* Submit Button */}
      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={handleSubmit}
          className="rounded py-2 px-4 text-white bg-blue-500 hover:bg-blue-600"
        >
          Submit
        </button>
      </div>

      {/* Loading Spinner */}
      <Backdrop sx={{ color: 'secondary.main', backgroundColor: 'rgba(255, 255, 255, 0.5)' }} open={loading}>
        <CircularProgress color="secondary" />
      </Backdrop>

      {/* Toast Message */}
      <ToastMessage
        message={toastMessage}
        severity={toastSeverity}
        open={openToast}
        handleClose={handleToastClose}
      />
    </div>
  );
};

export default ManualEntry;